import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import type { GateDecision, ReviewStatus } from '@/types';

// ─── Types ──────────────────────────────────────────────────────────────────

export interface ReviewQueueItem {
  id: string;
  file_name: string;
  /** AI gate decision — used for queue priority grouping. Null when the
   *  gate hasn't run yet (job paused at an earlier pipeline stage). */
  final_decision: GateDecision | null;
  review_status: ReviewStatus;
  blocking_count: number;
  alert_count: number;
  submitted_by_name: string | null;
  created_at: string;
  completed_at: string | null;
  reviewed_by: string | null;
  reviewed_by_name: string | null;
}

interface ReviewQueueResponse {
  items: ReviewQueueItem[];
}

// ─── Hooks ───────────────────────────────────────────────────────────────────

/**
 * Fetches all jobs with review_status = 'pending_review' or 'claimed' that need
 * human attention. Sorted by priority: REJECTED → ESCALATED →
 * MANUAL_REVIEW_REQUIRED → APPROVED_WITH_ALERTS → APPROVED, then by created_at ASC.
 *
 * FR-077: ALL gate decisions (including APPROVED) require human sign-off.
 */
export function useReviewQueue() {
  return useQuery<ReviewQueueItem[]>({
    queryKey: ['dashboard', 'review-queue'],
    queryFn: async () => {
      const res = await api.get<ReviewQueueResponse>('/api/v1/jobs/review-queue');
      return res.items;
    },
    refetchInterval: 30_000,
    refetchIntervalInBackground: true,
    staleTime: 15_000,
  });
}

/**
 * Mutation to claim a job for review (PATCH /api/v1/jobs/:id/claim).
 * On success, invalidates the review queue so the assignee name updates.
 */
export function useClaimJob() {
  const qc = useQueryClient();
  return useMutation<unknown, Error, string>({
    mutationFn: (jobId: string) => api.patch<unknown>(`/api/v1/jobs/${jobId}/claim`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['dashboard', 'review-queue'] });
      void qc.invalidateQueries({ queryKey: ['dashboard', 'review-queue-stats'] });
    },
  });
}
